import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { SureLcSsoUser } from '../../../core/models/sso.models';
import { SsoApiService } from '../../../core/services/http/sso-api.service';
import {
  MasterAdvisorAgreement,
  OnboardingCarrierContractingSaveRequest,
  OnboardingChecklist,
  OnboardingProductionAndAumSaveRequest,
  OnboardingTermsOfUse,
} from '../models/onboarding-models';
import { OnboardingStepKey } from '../onboarding-utils';

@Injectable({
  providedIn: 'root'
})
export class OnboardingApiService {
  private readonly http = inject(HttpClient);
  private readonly ssoApiService = inject(SsoApiService);

  // Cached observables: the wizard's step components re-mount every time
  // the user navigates to a route, so without caching we'd refire these
  // GETs each visit. `shareReplay(1)` multicasts the in-flight Observable
  // and replays the last emission to subsequent subscribers; clearing the
  // field invalidates the cache. Mutating writes refresh the cache inline
  // when their response carries the fresh value.
  private checklist$?: Observable<OnboardingChecklist>;
  private sureLcUser$?: Observable<SureLcSsoUser>;
  private termsOfUse$?: Observable<OnboardingTermsOfUse>;
  private masterAdvisorAgreement$?: Observable<MasterAdvisorAgreement>;

  // Captures whether ARC was missing the agent's SSN at the very first
  // checklist load. We hide the SSN field when ARC already has one — but a
  // successful AdvisorInformation save flips HasSsn to true in the cached
  // response, which without this signal would also hide the field mid-
  // session and leave the agent no way to fix a typo. Stored in
  // sessionStorage so the flag survives navigating between wizard steps
  // even if Angular tears down and recreates this service.
  private static readonly SSN_REQUIRED_KEY = 'onboarding.ssnRequiredAtEntry';

  private get ssnRequiredAtEntry(): boolean | null {
    const stored = sessionStorage.getItem(OnboardingApiService.SSN_REQUIRED_KEY);
    return stored === null ? null : stored === 'true';
  }

  private set ssnRequiredAtEntry(value: boolean) {
    sessionStorage.setItem(OnboardingApiService.SSN_REQUIRED_KEY, String(value));
  }

  getUsersRequiredSteps(): Observable<OnboardingStepKey[]> {
    return this.http.get<OnboardingStepKey[]>('/api/UserOnboarding/V2/Stages');
  }

  // Terms of Use API calls
  getLoggedInUserTermsOfUse(): Observable<OnboardingTermsOfUse> {
    if (!this.termsOfUse$) {
      this.termsOfUse$ = this.http
        .get<OnboardingTermsOfUse>('/api/UserOnboarding/TermsOfUse/LoggedInUser')
        .pipe(shareReplay(1));
    }
    return this.termsOfUse$;
  }

  getTermsOfUse(): Observable<OnboardingTermsOfUse> {
    return this.http.get<OnboardingTermsOfUse>('/api/UserOnboarding/TermsOfUse');
  }

  acceptTermsOfUse(): Observable<OnboardingTermsOfUse> {
    return this.http
      .post<OnboardingTermsOfUse>('/api/UserOnboarding/TermsOfUse', null)
      .pipe(tap(record => this.termsOfUse$ = of(record)));
  }

  // Master Advisor Agreement API calls
  getLoggedInUserMasterAdvisorAgreement(): Observable<MasterAdvisorAgreement> {
    if (!this.masterAdvisorAgreement$) {
      this.masterAdvisorAgreement$ = this.http
        .get<MasterAdvisorAgreement>('/api/MasterAdvisorAgreement/LoggedInUser')
        .pipe(shareReplay(1));
    }
    return this.masterAdvisorAgreement$;
  }

  initMasterAdvisorAgreement(): Observable<string> {
    return this.http.get('/api/MasterAdvisorAgreement', {
      params: {
        workflow: 'ror',
        fromNewPortal: false,
        fromFocPortal: true
      },
      responseType: 'text' as const
    });
  }

  createMasterAdvisorAgreement(envelopeData: MasterAdvisorAgreement): Observable<unknown> {
    // POST returns no useful payload; invalidate the cache so the next read
    // refetches with the updated IsDocusignCompleted flag.
    return this.http
      .post('/api/MasterAdvisorAgreement', envelopeData)
      .pipe(tap(() => this.masterAdvisorAgreement$ = undefined));
  }

  // Onboarding Checklist (Advisor Information + Carrier Contracting).
  // The GET returns the full checklist; each wizard step picks what it needs.
  // The two PUTs return the refreshed checklist and refresh the cache.
  getOnboardingChecklist(): Observable<OnboardingChecklist> {
    if (!this.checklist$) {
      this.checklist$ = this.http
        .get<OnboardingChecklist>('/api/UserOnboarding/Checklist')
        .pipe(
          tap(checklist => {
            // Capture once, on the very first load — the PUTs overwrite
            // checklist$ but bypass this tap, so subsequent saves don't
            // change the value.
            if (this.ssnRequiredAtEntry === null) {
              this.ssnRequiredAtEntry = !checklist.HasSsn;
            }
          }),
          shareReplay(1),
        );
    }
    return this.checklist$;
  }

  /**
   * True when ARC was missing the agent's SSN at wizard entry. Stays true
   * even after a successful Advisor Information save so the agent can fix
   * a typo without leaving and re-entering the wizard.
   */
  isSsnRequiredForWizard(): boolean {
    return this.ssnRequiredAtEntry ?? false;
  }

  /**
   * Clear the wizard-session SSN flag. Call when the user completes the
   * wizard or logs out so a future session re-evaluates HasSsn fresh.
   */
  clearWizardSession(): void {
    sessionStorage.removeItem(OnboardingApiService.SSN_REQUIRED_KEY);
    this.typedSsn = '';
  }

  // In-memory cache of what the user has typed in the SSN field, so the
  // Advisor Information step can repopulate the masked input when the user
  // navigates between wizard steps. Deliberately NOT persisted to
  // sessionStorage — the SSN is sensitive PII and shouldn't live at rest
  // in client-side storage past in-DOM exposure. A page refresh wipes the
  // value (acceptable: the agent re-types) but step-to-step nav is fine.
  private typedSsn = '';

  getTypedSsn(): string {
    return this.typedSsn;
  }

  setTypedSsn(value: string): void {
    this.typedSsn = value;
  }

  saveAdvisorInformation(payload: OnboardingProductionAndAumSaveRequest): Observable<OnboardingChecklist> {
    return this.http
      .put<OnboardingChecklist>('/api/UserOnboarding/Checklist/AdvisorInformation', payload)
      .pipe(tap(refreshed => this.checklist$ = of(refreshed)));
  }

  saveCarrierContracting(payload: OnboardingCarrierContractingSaveRequest): Observable<OnboardingChecklist> {
    return this.http
      .put<OnboardingChecklist>('/api/UserOnboarding/Checklist/CarrierContracting', payload)
      .pipe(tap(refreshed => this.checklist$ = of(refreshed)));
  }

  // Wraps SsoApiService.getSureLcUser with a session-scoped cache so the
  // Carrier Contracting step doesn't refetch the SureLC user every time it
  // re-mounts during the wizard flow.
  getSureLcUser(): Observable<SureLcSsoUser> {
    if (!this.sureLcUser$) {
      this.sureLcUser$ = this.ssoApiService.getSureLcUser().pipe(shareReplay(1));
    }
    return this.sureLcUser$;
  }
}
